/**
 * career-processes — the Prozess-Landkarten surface (route `/console/processes/:slug`).
 *
 * WHAT IT IS. One authored process graph per user use case, drawn from `GET /console/processes`,
 * with every node openable: the seat it runs on (integration key + the LIVE engine/model the boot
 * resolved), the prompt it sends (verbatim, at the exact `@N` version), and the REAL captured
 * calls that landed under it — joined client-side out of the caller's own `GET /api/runs` read.
 * Two cold reads, no store, no push. Operator-only: the server 403s `/console/processes` for
 * anyone who may not see provider cost, and that 403 — not route visibility — is the boundary.
 *
 * WHY THE WIRE IS RE-PARSED HERE. `app-elements` does not depend on `@career/contract-v2` (it has
 * never been a dependency of this package), so the ArkType output type cannot be imported and the
 * response is an untrusted record like every other. It is narrowed defensively, exactly the way
 * `career-runs.ts` narrows a run: a node whose `kind` is not one this surface can classify is
 * DROPPED rather than guessed into a shape, and an edge whose endpoints are gone draws nothing.
 *
 * LAYOUT (§0 "seamless document over cards"): the graph nodes are the ONE boxed thing on the page,
 * and deliberately so — a node is a real affordance (a button you click, focus and operate by
 * keyboard), which is precisely the exception the guideline reserves containment for. Everything
 * around it — the map switcher, the legend, the disclosure panel, the traces — is structured by
 * typography, spacing and hairlines, with the scrolling `<pre>` of a prompt or a reply as the only
 * other containment, the same one `/runs` keeps.
 */

import {
  KitElement,
  signal,
  type ActionMap,
  type KitContext,
  type ReadonlySignal,
  type Signal,
  type ViewModel,
} from "@platform/web-kit"
import { classMap, html, nothing, repeat, type TemplateResult } from "@platform/web-kit/render"
import { skeletonRows } from "../lib/skeleton"
import {
  NODE_KIND_KEY,
  PROCESS_EDGE_KINDS,
  PROCESS_NODE_KINDS,
  processGraph,
  processLegend,
  type ProcessEdgeKind,
  type ProcessEdgeView,
  type ProcessMapView,
  type ProcessNodeKind,
  type ProcessNodeView,
} from "../lib/process-graph"
import { ENGINE_KEY, engineChip, fmtEur, fmtNum, fmtWhen, paramGrid, tokenBar, type RunEngine, type RunStepView } from "../lib/run-trace"
import { parseRun, type RunView } from "./career-runs"
import { I18nToken, RouterToken, apiGet } from "../services"
import type { Router } from "@platform/web-kit"
import type { CareerI18n, CareerKey } from "../../i18n"

/** The URN a slug is bound to by the route table — a change REBINDS the VM (KitElement §4). */
export const PROCESS_URN_PREFIX = "urn:career:process:"

/**
 * The literal the prompt estate substitutes where the runtime appends German grounding doctrine.
 * Kept in sync with `api-v2/src/prompts/estate.ts` by eye — a drifted string only costs the visual
 * marker (the text still renders verbatim), never correctness.
 */
export const GROUNDING_PLACEHOLDER = "[…German grounding doctrine appended at runtime (§2)…]"

/** How many captured calls one node discloses before it starts counting instead. */
export const TRACE_LIMIT = 5

// ─── Client view types (parsed from the untrusted wire record) ──────────────────

export interface PromptTextView {
  id: string
  system: string
  user?: string
}

export interface SeamBindingView {
  key: string
  engine: string
  model: string
  label: string
  description: string
}

export interface ProcessConsoleView {
  maps: ProcessMapView[]
  prompts: PromptTextView[]
  seams: SeamBindingView[]
}

const str = (v: unknown): string | undefined => (typeof v === "string" && v.length > 0 ? v : undefined)
const num = (v: unknown): number | undefined => (typeof v === "number" && Number.isFinite(v) ? v : undefined)

const isNodeKind = (v: unknown): v is ProcessNodeKind => PROCESS_NODE_KINDS.some((k) => k === v)
const isEdgeKind = (v: unknown): v is ProcessEdgeKind => PROCESS_EDGE_KINDS.some((k) => k === v)

function parseNode(raw: unknown): ProcessNodeView | null {
  if (!raw || typeof raw !== "object") return null
  const n = raw as Record<string, unknown>
  const id = str(n["id"])
  const label = str(n["label"])
  const note = str(n["note"])
  const kind = n["kind"]
  // An unclassifiable node is dropped, never coerced: drawing it under some other kind's colour
  // would tell the operator "this is a model call" (or "this is only an event") on a guess.
  if (!id || !label || !note || !isNodeKind(kind)) return null
  const stepLabels = Array.isArray(n["stepLabels"]) ? (n["stepLabels"] as unknown[]).flatMap((l) => (str(l) ? [str(l)!] : [])) : undefined
  return {
    id,
    label,
    kind,
    note,
    layer: num(n["layer"]) ?? 0,
    lane: num(n["lane"]) ?? 0,
    ...(str(n["integrationKey"]) ? { integrationKey: str(n["integrationKey"]) } : {}),
    ...(str(n["promptId"]) ? { promptId: str(n["promptId"]) } : {}),
    ...(str(n["activityType"]) ? { activityType: str(n["activityType"]) } : {}),
    ...(stepLabels && stepLabels.length > 0 ? { stepLabels } : {}),
    ...(str(n["route"]) ? { route: str(n["route"]) } : {}),
    ...(n["deterministic"] === true ? { deterministic: true } : {}),
  }
}

function parseEdge(raw: unknown): ProcessEdgeView | null {
  if (!raw || typeof raw !== "object") return null
  const e = raw as Record<string, unknown>
  const from = str(e["from"])
  const to = str(e["to"])
  const label = str(e["label"])
  if (!from || !to || !label) return null
  // `kind` is REQUIRED on the wire (the api projection resolves the authored default), so the
  // `flow` here is the untrusted-record floor, not a second copy of the defaulting rule.
  return { from, to, label, kind: isEdgeKind(e["kind"]) ? e["kind"] : "flow" }
}

export function parseMap(raw: unknown): ProcessMapView | null {
  if (!raw || typeof raw !== "object") return null
  const m = raw as Record<string, unknown>
  const slug = str(m["slug"])
  const title = str(m["title"])
  if (!slug || !title) return null
  const nodes = Array.isArray(m["nodes"]) ? (m["nodes"] as unknown[]).map(parseNode).filter((n): n is ProcessNodeView => n !== null) : []
  const known = new Set(nodes.map((n) => n.id))
  const edges = Array.isArray(m["edges"])
    ? (m["edges"] as unknown[]).map(parseEdge).filter((e): e is ProcessEdgeView => e !== null && known.has(e.from) && known.has(e.to))
    : []
  return { slug, title, summary: str(m["summary"]) ?? "", nodes, edges }
}

function parsePrompt(raw: unknown): PromptTextView | null {
  if (!raw || typeof raw !== "object") return null
  const p = raw as Record<string, unknown>
  const id = str(p["id"])
  if (!id) return null
  return { id, system: typeof p["system"] === "string" ? p["system"] : "", ...(str(p["user"]) ? { user: str(p["user"]) } : {}) }
}

function parseSeam(raw: unknown): SeamBindingView | null {
  if (!raw || typeof raw !== "object") return null
  const s = raw as Record<string, unknown>
  const key = str(s["key"])
  if (!key) return null
  return {
    key,
    engine: str(s["engine"]) ?? "",
    model: str(s["model"]) ?? "",
    label: str(s["label"]) ?? key,
    description: str(s["description"]) ?? "",
  }
}

export function parseConsole(raw: unknown): ProcessConsoleView {
  const r = (raw ?? {}) as Record<string, unknown>
  return {
    maps: Array.isArray(r["maps"]) ? (r["maps"] as unknown[]).map(parseMap).filter((m): m is ProcessMapView => m !== null) : [],
    prompts: Array.isArray(r["prompts"]) ? (r["prompts"] as unknown[]).map(parsePrompt).filter((p): p is PromptTextView => p !== null) : [],
    seams: Array.isArray(r["seams"]) ? (r["seams"] as unknown[]).map(parseSeam).filter((s): s is SeamBindingView => s !== null) : [],
  }
}

// ─── The node ↔ trace join ──────────────────────────────────────────────────────

/** One captured call attributed to a node, with the run context it was captured in. */
export interface NodeTrace {
  readonly runId: string
  readonly createdAt: string
  readonly credits: number
  readonly stepIndex: number
  readonly step: RunStepView
}

/**
 * Does this captured step belong to this node?
 *
 * THREE KEYS, and the exactness of each one is load-bearing:
 *
 *  1. `activityType` — the run's activity must be the node's. Without it, `block-probe`
 *     (`workshop-interview-round`) and `block-nachziehen` (`workshop-interview-stimulus`) merge:
 *     they carry IDENTICAL step labels and are separated by activity alone.
 *  2. `stepLabels` — EXACT string equality, never `startsWith`/`includes` on the step's label.
 *     `planner-seam` writes `workshop-round-planner` and `planner-floor` writes
 *     `workshop-round-planner-fallback` under the SAME activity — one label is a prefix of the
 *     other. A prefix match would show every failed-planner call under the successful seam too,
 *     destroying the one distinction that node pair exists to draw: "the model tried and failed"
 *     versus "this seat is deterministic".
 *  3. `promptRef` — the prompt-artifact version that authored the call. Applied only when BOTH
 *     sides have one, so an older capture that predates `promptRef` is included rather than
 *     silently dropped.
 *
 * A node with no `activityType` matches NOTHING, on purpose — see `sektion-floor`, whose floor
 * writes the same step label as the successful call, so there is no honest filter for it at all.
 */
export function nodeMatchesStep(node: ProcessNodeView, runActivityType: string | undefined, step: RunStepView): boolean {
  if (!node.activityType) return false
  if (runActivityType !== node.activityType) return false
  const labels = node.stepLabels
  if (labels && labels.length > 0) {
    if (step.label === undefined) return false
    if (!labels.some((l) => l === step.label)) return false
  }
  if (node.promptId !== undefined && step.promptRef !== undefined && step.promptRef !== node.promptId) return false
  return true
}

/** Every captured call attributed to `node`, newest run first (the server's own order). */
export function tracesForNode(node: ProcessNodeView, runs: readonly RunView[]): NodeTrace[] {
  const out: NodeTrace[] = []
  for (const run of runs) {
    run.steps.forEach((step, stepIndex) => {
      if (nodeMatchesStep(node, run.activityType, step)) {
        out.push({ runId: run.id, createdAt: run.createdAt, credits: run.credits, stepIndex, step })
      }
    })
  }
  return out
}

/** Why a node shows no traces — three honest answers, never a blank region. */
export type EmptyTraceReason = "no-model-call" | "no-honest-filter" | "nothing-captured"

export function emptyTraceReason(node: ProcessNodeView, matches: number): EmptyTraceReason | null {
  if (matches > 0) return null
  if (node.kind !== "ai-seam") return "no-model-call"
  if (!node.activityType) return "no-honest-filter"
  return "nothing-captured"
}

const EMPTY_TRACE_KEY: Record<EmptyTraceReason, CareerKey> = {
  "no-model-call": "processes.traces.noModelCall",
  "no-honest-filter": "processes.traces.noFilter",
  "nothing-captured": "processes.traces.none",
}

// ─── ViewModel ──────────────────────────────────────────────────────────────────

export class ProcessesVm implements ViewModel {
  readonly console: Signal<ProcessConsoleView | null> = signal<ProcessConsoleView | null>(null)
  readonly runs: Signal<readonly RunView[]> = signal<readonly RunView[]>([])
  /** A failed `/api/runs` read degrades the traces only — the graph itself still renders. */
  readonly runsError: Signal<string | null> = signal<string | null>(null)
  readonly rate: Signal<number> = signal(0.92)
  readonly selected: Signal<string | null> = signal<string | null>(null)
  readonly error: Signal<string | null> = signal<string | null>(null)
  readonly loading: Signal<boolean> = signal(true)
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly t: CareerI18n["t"]

  constructor(
    /** The slug from the route, or null on the bare `/console/processes` entry. */
    readonly slug: string | null,
    i18n: CareerI18n,
    private readonly router: Router | null = null,
  ) {
    this.t = i18n.t
    this.renderDeps = [this.console, this.runs, this.runsError, this.rate, this.selected, this.error, this.loading]
  }

  attach(): () => void {
    void this.load()
    return () => {}
  }

  async load(): Promise<void> {
    this.loading.set(true)
    this.error.set(null)
    try {
      this.console.set(parseConsole(await apiGet<unknown>("/api/console/processes")))
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.loading.set(false)
    }
    // Traces are a SEPARATE read and a separate failure: the graph, the seats and the prompts are
    // worth reading even when the run history is unavailable.
    try {
      const data = await apiGet<{ runs: unknown; usdEurRate: unknown }>("/api/runs")
      this.runs.set(Array.isArray(data.runs) ? data.runs.map(parseRun).filter((r): r is RunView => r !== null) : [])
      this.rate.set(typeof data.usdEurRate === "number" && data.usdEurRate > 0 ? data.usdEurRate : 0.92)
      this.runsError.set(null)
    } catch (e) {
      this.runsError.set(e instanceof Error ? e.message : String(e))
    }
  }

  /** The map the route asked for; the FIRST authored map on the bare entry (never a dead page). */
  map(): ProcessMapView | null {
    const maps = this.console.value?.maps ?? []
    if (maps.length === 0) return null
    return (this.slug ? maps.find((m) => m.slug === this.slug) : undefined) ?? maps[0] ?? null
  }

  node(id: string | null): ProcessNodeView | null {
    if (!id) return null
    return this.map()?.nodes.find((n) => n.id === id) ?? null
  }

  seam(key: string | undefined): SeamBindingView | null {
    if (!key) return null
    return this.console.value?.seams.find((s) => s.key === key) ?? null
  }

  prompt(id: string | undefined): PromptTextView | null {
    if (!id) return null
    return this.console.value?.prompts.find((p) => p.id === id) ?? null
  }

  traces(node: ProcessNodeView): NodeTrace[] {
    return tracesForNode(node, this.runs.value)
  }

  select(id: string): void {
    this.selected.set(this.selected.value === id ? null : id)
  }

  go(url: string): void {
    void this.router?.navigate(url)
  }
}

// ─── Element ────────────────────────────────────────────────────────────────────

export class CareerProcesses extends KitElement<ProcessesVm> {
  protected createViewModel(ctx: KitContext): ProcessesVm {
    const urn = this.urn
    const slug = urn && urn.startsWith(PROCESS_URN_PREFIX) ? decodeURIComponent(urn.slice(PROCESS_URN_PREFIX.length)) : null
    return new ProcessesVm(slug && slug.length > 0 ? slug : null, ctx.inject(I18nToken), ctx.inject(RouterToken))
  }

  /** The other authored maps, as plain links — a switcher, not a tab chrome. */
  private mapNav(vm: ProcessesVm, maps: readonly ProcessMapView[], current: ProcessMapView): TemplateResult {
    return html`
      <nav class="pg-maps" aria-label=${vm.t("processes.maps.title")}>
        ${repeat(
          maps,
          (m) => m.slug,
          (m) => {
            const url = `/console/processes/${m.slug}`
            return html`<a
              class=${classMap({ "pg-map-link": true, "is-current": m.slug === current.slug })}
              href=${url}
              data-act="nav"
              data-url=${url}
              aria-current=${m.slug === current.slug ? "page" : nothing}
              lang="de"
              >${m.title}</a
            >`
          },
        )}
      </nav>
    `
  }

  /**
   * The prompt as the seam sends it, with the runtime seam MARKED. The estate substitutes a
   * placeholder where German grounding doctrine is appended at call time, so what is rendered
   * here is shorter than what the model actually receives — saying so is the difference between
   * a disclosure and a half-truth.
   */
  private promptPre(vm: ProcessesVm, text: string): TemplateResult {
    const parts = text.split(GROUNDING_PLACEHOLDER)
    if (parts.length === 1) return html`<pre class="run-prompt-pre">${text}</pre>`
    return html`
      <pre class="run-prompt-pre">${parts.map(
        (part, i) =>
          html`${i === 0 ? nothing : html`<mark class="pg-prompt-runtime" lang="de">${vm.t("processes.prompt.runtimeMark")}</mark>`}${part}`,
      )}</pre>
      <p class="pg-note" lang="de">${vm.t("processes.prompt.runtimeNote")}</p>
    `
  }

  private seamBlock(vm: ProcessesVm, node: ProcessNodeView): TemplateResult {
    if (!node.integrationKey) {
      return html`
        <section class="pg-block">
          <h4 class="run-section-label" lang="de">${vm.t("processes.seam.title")}</h4>
          <p class="pg-note" lang="de">${vm.t("processes.seam.none")}</p>
        </section>
      `
    }
    const seam = vm.seam(node.integrationKey)
    return html`
      <section class="pg-block">
        <h4 class="run-section-label" lang="de">${vm.t("processes.seam.title")}</h4>
        <p class="pg-seam-key"><code>${node.integrationKey}</code></p>
        ${seam
          ? html`
              <p class="pg-seam-live">
                ${seamEngineChip(seam.engine, vm.t)}
                <span class="pg-seam-model">${seam.model}</span>
              </p>
              <p class="pg-seam-label" lang="de"><b>${seam.label}</b> — ${seam.description}</p>
            `
          : html`<p class="pg-note" lang="de">${vm.t("processes.seam.unresolved")}</p>`}
      </section>
    `
  }

  private promptBlockFor(vm: ProcessesVm, node: ProcessNodeView): TemplateResult {
    if (!node.promptId) {
      return html`
        <section class="pg-block">
          <h4 class="run-section-label" lang="de">${vm.t("processes.prompt.title")}</h4>
          <p class="pg-note" lang="de">${vm.t("processes.prompt.none")}</p>
        </section>
      `
    }
    const prompt = vm.prompt(node.promptId)
    return html`
      <section class="pg-block">
        <h4 class="run-section-label" lang="de">${vm.t("processes.prompt.title")}</h4>
        <p class="pg-prompt-id"><code>${node.promptId}</code></p>
        ${prompt
          ? html`
              <p class="run-section-label" lang="de">${vm.t("processes.prompt.system")}</p>
              ${this.promptPre(vm, prompt.system)}
              ${prompt.user
                ? html`
                    <p class="run-section-label" lang="de">${vm.t("processes.prompt.user")}</p>
                    ${this.promptPre(vm, prompt.user)}
                  `
                : nothing}
            `
          : html`<p class="pg-note" lang="de">${vm.t("processes.prompt.missing", { id: node.promptId })}</p>`}
      </section>
    `
  }

  private trace(vm: ProcessesVm, trace: NodeTrace, rate: number): TemplateResult {
    const { step } = trace
    return html`
      <li class="pg-trace">
        <div class="pg-trace-head">
          <span class="pg-trace-when">${fmtWhen(trace.createdAt)}</span>
          ${engineChip(step.engine, vm.t)}
          <span class="pg-trace-model">${step.model}</span>
          ${step.label ? html`<span class="run-label">${step.label}</span>` : nothing}
          ${step.costUsd !== undefined ? html`<span class="pg-trace-cost">${fmtEur(step.costUsd, rate)}</span>` : nothing}
        </div>
        <p class="pg-trace-figures" lang="de">
          ${vm.t("processes.trace.tokens", {
            input: step.promptTokens != null ? fmtNum(step.promptTokens) : "—",
            output: step.completionTokens != null ? fmtNum(step.completionTokens) : "—",
          })}${step.latencyMs != null ? ` · ${fmtNum(Math.round(step.latencyMs))} ms` : ""}
        </p>
        ${tokenBar(step)} ${paramGrid(step, vm.t)}
        ${step.completion
          ? html`
              <details class="pg-trace-completion">
                <summary lang="de">${vm.t("processes.trace.completion")}</summary>
                <pre class="run-completion-pre">${step.completion}</pre>
              </details>
            `
          : nothing}
      </li>
    `
  }

  private tracesBlock(vm: ProcessesVm, node: ProcessNodeView): TemplateResult {
    const all = vm.traces(node)
    const reason = emptyTraceReason(node, all.length)
    const runsError = vm.runsError.value
    return html`
      <section class="pg-block">
        <h4 class="run-section-label" lang="de">${vm.t("processes.traces.title")}</h4>
        ${runsError ? html`<p class="pg-note" lang="de">${vm.t("processes.traces.unavailable", { message: runsError })}</p>` : nothing}
        ${reason
          ? html`<p class="pg-note" lang="de">${vm.t(EMPTY_TRACE_KEY[reason])}</p>`
          : html`
              <p class="pg-trace-count" lang="de">
                ${vm.t("processes.traces.count", { shown: fmtNum(Math.min(all.length, TRACE_LIMIT)), total: fmtNum(all.length) })}
              </p>
              <ul class="pg-traces">
                ${repeat(
                  all.slice(0, TRACE_LIMIT),
                  (tr) => `${tr.runId}:${tr.stepIndex}`,
                  (tr) => this.trace(vm, tr, vm.rate.value),
                )}
              </ul>
            `}
      </section>
    `
  }

  private detail(vm: ProcessesVm): TemplateResult {
    const node = vm.node(vm.selected.value)
    if (!node) return html`<p class="pg-hint" lang="de">${vm.t("processes.select.hint")}</p>`
    return html`
      <article class="pg-detail">
        <header class="pg-detail-head">
          <h3 class="pg-detail-title" lang="de">${node.label}</h3>
          <span class="pg-detail-kind" lang="de">${vm.t(NODE_KIND_KEY[node.kind])}</span>
          ${node.deterministic ? html`<span class="pg-detail-kind is-deterministic" lang="de">${vm.t("processes.deterministic")}</span>` : nothing}
        </header>
        <p class="pg-detail-note" lang="de">${node.note}</p>
        ${node.route
          ? html`<p class="pg-detail-route"><span class="run-section-label" lang="de">${vm.t("processes.detail.route")}</span> <code>${node.route}</code></p>`
          : nothing}
        ${this.seamBlock(vm, node)} ${this.promptBlockFor(vm, node)} ${this.tracesBlock(vm, node)}
      </article>
    `
  }

  protected render(vm: ProcessesVm): TemplateResult {
    const err = vm.error.value
    const data = vm.console.value

    const body = ((): TemplateResult => {
      if (err) {
        return html`
          <div class="runs-error-box">
            <p class="surface-error" role="alert" lang="de">${vm.t("review.error", { message: err })}</p>
            <button type="button" data-act="reload-processes" lang="de">${vm.t("processes.retry")}</button>
          </div>
        `
      }
      if (vm.loading.value && data === null) {
        return html`<div class="pg-loading" aria-busy="true" aria-label=${vm.t("processes.loading")}>${skeletonRows(3)}</div>`
      }
      const map = vm.map()
      if (!data || !map) return html`<p class="surface-empty" lang="de">${vm.t("processes.empty")}</p>`
      return html`
        ${this.mapNav(vm, data.maps, map)}
        <h2 class="pg-map-title" lang="de">${map.title}</h2>
        <p class="pg-map-summary" lang="de">${map.summary}</p>
        ${processLegend(vm.t)} ${processGraph(map, { selectedId: vm.selected.value, t: vm.t })}
        <div class="pg-detail-region" aria-live="polite">${this.detail(vm)}</div>
      `
    })()

    return html`
      <section class="surface processes" aria-labelledby="processes-title">
        <h1 id="processes-title" class="surface-title" lang="de">${vm.t("surface.prozesse.title")}</h1>
        <p class="surface-lead" lang="de">${vm.t("surface.prozesse.lead")}</p>
        ${body}
      </section>
    `
  }

  protected override actions(vm: ProcessesVm): ActionMap {
    return {
      "select-node": ({ node }) => {
        if (node) vm.select(node)
      },
      "reload-processes": () => void vm.load(),
      nav: ({ url }, event) => {
        event.preventDefault()
        if (url) vm.go(url)
      },
    }
  }
}

/**
 * The seat's LIVE engine as a chip. A token the run-trace vocabulary knows gets its German label;
 * anything else is shown RAW rather than folded into the nearest known engine — a seat that reads
 * "Deterministisch" when it is really something else is the one lie this panel must not tell.
 * A seat the boot did not resolve never reaches here at all: the api OMITS it, and the caller
 * renders `processes.seam.unresolved` for the missing entry.
 */
export function seamEngineChip(engine: string, t: CareerI18n["t"]): TemplateResult {
  const known = (Object.keys(ENGINE_KEY) as RunEngine[]).find((e) => e === engine)
  return known ? engineChip(known, t) : html`<span class="run-engine" lang="de">${engine}</span>`
}
